import { useEffect, useRef, useState } from 'preact/hooks';
import { Ban, Flag, MessageCircle, Send, Smile, UserRound, Volume2, VolumeX, X } from 'lucide-preact';
import { QUICK_CHAT } from '@vm/content';
import type { ChatMessage } from '@vm/shared';
import { social } from '../api/social';
import { audio } from '../../game/audio/Audio';
import { openModal } from '../state/nav';
import { blockUser, markChannelRead, muteUser, sendChat } from '../state/socialActions';
import { chatChannel, chatLog, chatOpen, chatUnread, friends, me, mutedUsers, party } from '../state/store';
import { fmtTime } from '../ui/format';
import { Badge, confirmDialog, Modal } from '../ui/kit';
import { openProfile, openReport } from './Profile';

interface ChannelTab {
  id: string;
  label: string;
}

function channelTabs(): ChannelTab[] {
  const tabs: ChannelTab[] = [{ id: 'global', label: 'Global' }];
  if (me.value?.clan) tabs.push({ id: 'clan', label: 'Clan' });
  if (party.value && party.value.members.length > 1) tabs.push({ id: 'party', label: 'Party' });
  const log = chatLog.value;
  const dms = new Set<string>(Object.keys(log).filter((k) => k.startsWith('dm:')));
  if (chatChannel.value.startsWith('dm:')) dms.add(chatChannel.value);
  for (const id of dms) {
    const uid = id.slice(3);
    const friend = friends.value?.friends.find((f) => f.id === uid);
    const fromMsg = log[id]?.find((m) => m.from.id === uid)?.from.name;
    tabs.push({ id, label: friend?.name ?? fromMsg ?? 'Message' });
  }
  return tabs;
}

function MessageMenu(props: { msg: ChatMessage; close: () => void }) {
  const m = props.msg;
  const muted = mutedUsers.value.has(m.from.id);
  return (
    <div class="menu-list">
      <button
        class="menu-item"
        onClick={() => {
          props.close();
          openProfile(m.from.id, m.from.name);
        }}
      >
        <UserRound size={20} /> View profile
      </button>
      <button
        class="menu-item"
        onClick={() => {
          muteUser(m.from.id, !muted, m.from.name);
          props.close();
        }}
      >
        {muted ? <Volume2 size={20} /> : <VolumeX size={20} />} {muted ? 'Unmute' : 'Mute (only for you)'}
      </button>
      <button
        class="menu-item danger"
        onClick={async () => {
          props.close();
          if (await confirmDialog({ title: `Block ${m.from.name}?`, body: "You won't see their messages or invites.", confirm: 'Block', danger: true })) await blockUser(m.from.id, m.from.name);
        }}
      >
        <Ban size={20} /> Block
      </button>
      <button
        class="menu-item danger"
        onClick={() => {
          props.close();
          openReport({ targetId: m.from.id, name: m.from.name });
        }}
      >
        <Flag size={20} /> Report message
      </button>
    </div>
  );
}

function Line(props: { msg: ChatMessage; mine: boolean }) {
  const m = props.msg;
  const staff = m.from.role === 'admin' || m.from.role === 'moderator';
  return (
    <div class={`chat-msg ${props.mine ? 'is-mine' : ''} ${staff ? 'is-staff' : ''}`}>
      <div class="chat-msg-head">
        {props.mine ? (
          <b class="chat-name">You</b>
        ) : (
          <button
            class="chat-name"
            onClick={() =>
              openModal((close) => (
                <Modal title={m.from.name} size="sm" onClose={close}>
                  <MessageMenu msg={m} close={close} />
                </Modal>
              ))
            }
          >
            {m.from.clanTag && <span class="chat-tag">[{m.from.clanTag}]</span>}
            {m.from.name}
            {staff && <span class="chat-staff">{m.from.role === 'admin' ? 'DEV' : 'MOD'}</span>}
          </button>
        )}
        <small>{fmtTime(m.ts)}</small>
      </div>
      <div class="chat-text">{m.text}</div>
    </div>
  );
}

/** Message list + composer for one channel (used by the drawer and the clan page). */
export function ChannelView(props: { channel: string; active: boolean }) {
  const channel = props.channel;
  const lines = chatLog.value[channel] ?? [];
  const m = me.value;
  const kid = m?.restricted ?? false;
  const mutedUntil = m?.mutedUntil && Date.parse(m.mutedUntil) > Date.now() ? m.mutedUntil : null;
  const [text, setText] = useState('');
  const [quick, setQuick] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const connected = social.state.value === 'open';

  useEffect(() => {
    if (props.active) markChannelRead(channel);
  }, [props.active, channel, lines.length]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length, channel, props.active]);

  const send = (t: string): void => {
    if (sendChat(channel, t)) {
      audio.play('ui_click');
      setText('');
      setQuick(false);
    }
  };
  return (
    <div class="chat-channel">
      <div class="chat-list" ref={listRef}>
        {!lines.length && <div class="chat-empty">{connected ? 'No messages yet. Say hello!' : 'Connecting to chat…'}</div>}
        {lines.map((l) => (
          <Line key={l.id} msg={l} mine={l.from.id === m?.id} />
        ))}
      </div>
      {!connected && lines.length > 0 && <div class="chat-status">Reconnecting…</div>}
      <footer class="chat-foot">
        {mutedUntil ? (
          <div class="chat-muted">You're muted until {new Date(mutedUntil).toLocaleString()}.</div>
        ) : (
          <>
            {(kid || quick) && (
              <div class="chat-quick">
                {QUICK_CHAT.map((q, i) => (
                  <button key={i} class="chat-quick-btn" onClick={() => send(`#q${i}`)}>
                    {q}
                  </button>
                ))}
              </div>
            )}
            {kid ? (
              <div class="chat-note">Quick chat keeps everyone safe. Tap a message to send it.</div>
            ) : (
              <form
                class="chat-input-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  send(text);
                }}
              >
                <button type="button" class={`btn icon sm ${quick ? 'is-on' : ''}`} aria-label="Quick chat" onClick={() => setQuick(!quick)}>
                  <Smile size={20} />
                </button>
                <input
                  class="ui-input"
                  value={text}
                  maxLength={120}
                  placeholder="Be kind — no personal info"
                  onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <button type="submit" class="btn primary icon sm" aria-label="Send" disabled={!text.trim()}>
                  <Send size={20} />
                </button>
              </form>
            )}
          </>
        )}
      </footer>
    </div>
  );
}

export function ChatDrawer() {
  const open = chatOpen.value;
  const ch = chatChannel.value;
  if (!open) return null;
  const tabs = channelTabs();
  const channel = tabs.some((t) => t.id === ch) ? ch : 'global';
  const close = (): void => {
    audio.play('ui_click');
    chatOpen.value = false;
  };
  return (
    <div class="chat-layer" onClick={close}>
      <aside class="chat-drawer" onClick={(e) => e.stopPropagation()} aria-label="Chat">
        <header class="chat-head">
          <MessageCircle size={22} />
          <div class="chat-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                class={`chat-tab ${t.id === channel ? 'is-on' : ''}`}
                onClick={() => {
                  audio.play('ui_click');
                  chatChannel.value = t.id;
                }}
              >
                {t.label}
                <Badge count={t.id === channel ? 0 : chatUnread.value[t.id] ?? 0} />
              </button>
            ))}
          </div>
          <button class="ui-close" onClick={close} aria-label="Close chat">
            <X size={22} strokeWidth={3} />
          </button>
        </header>
        <ChannelView channel={channel} active />
      </aside>
    </div>
  );
}
